import { useEffect, useState } from 'react'
import { CheckCircle, X, AlertCircle, Info, AlertTriangle } from 'lucide-react'

export interface ToastProps {
    id: string
    type: 'success' | 'error' | 'warning' | 'info'
    title: string
    message?: string
    duration?: number
    onClose: (id: string) => void
}

export const Toast = ({ id, type, title, message, duration = 5000, onClose }: ToastProps) => {
    const [isVisible, setIsVisible] = useState(false)

    useEffect(() => {
        // Mostrar toast com animação
        const showTimer = setTimeout(() => setIsVisible(true), 100)

        // Auto-fechar após duration
        const hideTimer = setTimeout(() => {
            setIsVisible(false)
            setTimeout(() => onClose(id), 300) // Aguarda animação de saída
        }, duration)

        return () => {
            clearTimeout(showTimer)
            clearTimeout(hideTimer)
        }
    }, [id, duration, onClose])

    const getIcon = () => {
        switch (type) {
            case 'success':
                return <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            case 'error':
                return <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
            case 'warning':
                return <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
            case 'info':
                return <Info className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            default:
                return <Info className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        }
    }

    const getBackgroundColor = () => {
        switch (type) {
            case 'success':
                return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
            case 'error':
                return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
            case 'warning':
                return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
            case 'info':
                return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
            default:
                return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
        }
    }

    return (
        <div
            className={`
                ${getBackgroundColor()}
                border rounded-lg shadow-lg p-4 mb-3 transition-all duration-300 ease-in-out
                ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
                max-w-sm w-full
            `}
        >
            <div className="flex items-start">
                <div className="flex-shrink-0 mr-3">
                    {getIcon()}
                </div>
                <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {title}
                    </h4>
                    {message && (
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                            {message}
                        </p>
                    )}
                </div>
                <button
                    onClick={() => {
                        setIsVisible(false)
                        setTimeout(() => onClose(id), 300)
                    }}
                    className="flex-shrink-0 ml-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    )
}


// Container de toasts
export const ToastContainer = ({ toasts }: { toasts: ToastProps[] }) => {
    return (
        <div className="fixed top-4 right-4 z-50 space-y-2">
            {toasts.map(toast => (
                <Toast key={toast.id} {...toast} />
            ))}
        </div>
    )
}

